import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Link2, Copy, ExternalLink, Building, ChevronDown, ChevronRight, Globe,
  ShieldCheck, Smartphone, Bike, Users, RefreshCw,
} from 'lucide-react';
import { adminApi } from '../../services/api';
import { rebaseToCurrentOrigin, getPublicOrigin } from '../../services/origin';

const PLATFORM_ICON = {
  superadmin_login: ShieldCheck,
  agent_login: Users,
  rider_login: Bike,
  staff_login: Smartphone,
};

export default function SuperadminPreviewLinks() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [details, setDetails] = useState({});  // brand_id -> full sections

  const load = () => {
    setLoading(true);
    adminApi.get('/superadmin/preview-links')
      .then(r => setData(r.data))
      .catch(() => toast.error('Errore caricamento'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const expand = async (brand_id) => {
    if (expanded === brand_id) { setExpanded(null); return; }
    setExpanded(brand_id);
    if (!details[brand_id]) {
      try {
        const r = await adminApi.get(`/superadmin/preview-links?brand_id=${brand_id}`);
        setDetails(d => ({ ...d, [brand_id]: r.data }));
      } catch { toast.error('Errore caricamento brand'); }
    }
  };

  const copy = (url) => { navigator.clipboard.writeText(url); toast.success('Link copiato'); };

  // Rebase every URL (platform + per-brand) to the current page origin so
  // every "open / copy" link in this page points to the same domain the
  // superadmin is browsing.
  const platformLinks = useMemo(() =>
    (data?.platform_links || []).map(it => ({ ...it, url: rebaseToCurrentOrigin(it.url) })),
    [data]
  );
  const rebaseSections = (sections) => sections.map(s => ({
    ...s,
    items: s.items.map(it => ({ ...it, url: rebaseToCurrentOrigin(it.url) })),
  }));
  const currentOrigin = getPublicOrigin();

  if (loading) return <div className="p-6 md:p-8 text-[#94A3B8] text-sm">Caricamento…</div>;
  if (!data) return null;

  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-5xl" data-testid="superadmin-preview-links-page">
      <div className="mb-5 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl tracking-tight font-bold text-white">Anteprima &amp; link</h1>
          <p className="text-[#94A3B8] text-xs mt-0.5">
            Tutti gli URL della piattaforma in chiaro. Espandi un brand per vedere
            i suoi link cliente, campagne e operatori.
          </p>
        </div>
        <button onClick={load} data-testid="reload-superadmin-links"
          className="h-9 px-3 inline-flex items-center gap-1.5 rounded-xl bg-[#334155] text-xs font-medium text-white hover:bg-[#475569]">
          <RefreshCw size={13} /> Ricarica
        </button>
      </div>

      {/* Current-domain banner */}
      <div data-testid="sa-current-domain-banner"
        className="mb-5 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
          <Globe size={16} className="text-emerald-300" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] text-emerald-300/80 font-medium uppercase tracking-wide">Dominio attivo</p>
          <p className="text-sm text-white font-mono truncate" data-testid="sa-current-domain-value">{currentOrigin.replace(/^https?:\/\//, '')}</p>
        </div>
        <button onClick={() => copy(currentOrigin)} data-testid="sa-copy-current-domain"
          className="h-8 px-3 inline-flex items-center gap-1.5 rounded-lg border border-emerald-400/40 text-xs font-medium text-emerald-300 hover:bg-emerald-500/10">
          <Copy size={12} /> Copia
        </button>
      </div>

      {/* Platform-wide links */}
      <section className="mb-6" data-testid="platform-links-section">
        <h2 className="text-sm font-semibold text-white mb-2 flex items-center gap-1.5">
          <Globe size={14} className="text-[#0EA5E9]" /> Piattaforma Lazo
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {platformLinks.map(it => {
            const Icon = PLATFORM_ICON[it.key] || Link2;
            return (
              <div key={it.key} data-testid={`platform-link-${it.key}`}
                className="bg-[#1E293B] rounded-2xl border border-[#334155] p-4 hover:border-[#475569] transition-colors">
                <div className="flex items-start gap-3 mb-2">
                  <div className="w-9 h-9 rounded-xl bg-[#D44E20]/15 flex items-center justify-center flex-shrink-0">
                    <Icon size={15} className="text-[#D44E20]" />
                  </div>
                  <p className="text-sm font-semibold text-white truncate">{it.label}</p>
                </div>
                <div className="flex items-center gap-2">
                  <input type="text" value={it.url} readOnly
                    className="flex-1 h-8 bg-[#0F172A] border border-[#334155] rounded-lg px-2.5 text-[11px] font-mono text-white focus:outline-none truncate" />
                  <button onClick={() => copy(it.url)} title="Copia"
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#334155] text-[#94A3B8] hover:text-white">
                    <Copy size={13} />
                  </button>
                  <a href={it.url} target="_blank" rel="noopener noreferrer" title="Apri"
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#D44E20] text-white hover:bg-[#B83D15]">
                    <ExternalLink size={13} />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Per-brand links */}
      <section data-testid="brands-list-section">
        <h2 className="text-sm font-semibold text-white mb-2 flex items-center gap-1.5">
          <Building size={14} className="text-[#0EA5E9]" /> Brand ({data.brands.length})
        </h2>
        <div className="space-y-2">
          {data.brands.map(b => (
            <div key={b.brand_id} data-testid={`brand-row-${b.brand_id}`}
              className="bg-[#1E293B] rounded-2xl border border-[#334155] overflow-hidden">
              <button onClick={() => expand(b.brand_id)}
                data-testid={`expand-brand-${b.brand_id}`}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#0F172A]/40 transition-colors text-left">
                {expanded === b.brand_id ? <ChevronDown size={15} className="text-[#94A3B8]" /> : <ChevronRight size={15} className="text-[#94A3B8]" />}
                <Building size={14} className="text-[#0EA5E9]" />
                <p className="text-sm font-semibold text-white flex-1">{b.name}</p>
                <span className="text-[10px] text-[#94A3B8] font-mono">{b.brand_id.slice(-8)}</span>
              </button>
              {expanded === b.brand_id && (
                <div className="px-4 pb-4 pt-1 border-t border-[#334155]">
                  {!details[b.brand_id] ? (
                    <p className="text-[11px] text-[#94A3B8] py-3">Caricamento…</p>
                  ) : (
                    <div className="space-y-3 mt-2">
                      {rebaseSections(details[b.brand_id].sections).map(sec => (
                        <div key={sec.title}>
                          <p className="text-[11px] font-semibold text-[#94A3B8] mb-1.5 uppercase tracking-wide">{sec.title}</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {sec.items.map(it => (
                              <div key={it.key} data-testid={`sa-link-${b.brand_id}-${it.key}`}
                                className="bg-[#0F172A] border border-[#334155] rounded-xl p-3">
                                <p className="text-xs font-semibold text-white mb-1 truncate">{it.label}</p>
                                {it.subtitle && <p className="text-[10px] text-[#64748B] mb-1.5 line-clamp-1">{it.subtitle}</p>}
                                <div className="flex items-center gap-1.5">
                                  <input type="text" value={it.url} readOnly
                                    className="flex-1 h-7 bg-[#1E293B] border border-[#334155] rounded-md px-2 text-[10px] font-mono text-white focus:outline-none truncate" />
                                  <button onClick={() => copy(it.url)} title="Copia"
                                    className="w-7 h-7 flex items-center justify-center rounded-md bg-[#334155] text-[#94A3B8] hover:text-white">
                                    <Copy size={11} />
                                  </button>
                                  <a href={it.url} target="_blank" rel="noopener noreferrer" title="Apri"
                                    className="w-7 h-7 flex items-center justify-center rounded-md bg-[#D44E20] text-white">
                                    <ExternalLink size={11} />
                                  </a>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
